import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  MessageCircle, Facebook, Instagram, Twitter, Youtube, Globe, Mail,
  Settings, Check, X, ExternalLink, RefreshCw, AlertTriangle, Key,
  Phone, MessageSquare, FileText, Link2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const channelConfig = {
  whatsapp: {
    name: 'WhatsApp (Twilio)',
    icon: MessageCircle,
    color: 'bg-green-500',
    description: 'Connect via Twilio for WhatsApp messaging',
    isTwilio: true,
    settingsUrl: '/whatsapp-settings',
    fields: [],
    docsUrl: 'https://www.twilio.com/docs/whatsapp/quickstart'
  },
  facebook: {
    name: 'Facebook Messenger',
    icon: Facebook,
    color: 'bg-blue-600',
    description: 'Connect your Facebook Page for Messenger conversations',
    fields: [
      { key: 'page_id', label: 'Page ID', type: 'text', required: true },
      { key: 'page_access_token', label: 'Page Access Token', type: 'password', required: true },
      { key: 'app_id', label: 'App ID (optional)', type: 'text', required: false },
      { key: 'app_secret', label: 'App Secret (optional)', type: 'password', required: false }
    ],
    docsUrl: 'https://developers.facebook.com/docs/messenger-platform/getting-started'
  },
  instagram: {
    name: 'Instagram DMs',
    icon: Instagram,
    color: 'bg-pink-500',
    description: 'Connect Instagram Business for direct messages',
    fields: [
      { key: 'page_id', label: 'Instagram Business Account ID', type: 'text', required: true },
      { key: 'page_access_token', label: 'Access Token', type: 'password', required: true },
      { key: 'app_id', label: 'App ID (optional)', type: 'text', required: false }
    ],
    docsUrl: 'https://developers.facebook.com/docs/instagram-api/getting-started'
  },
  twitter: {
    name: 'X (Twitter)',
    icon: Twitter,
    color: 'bg-sky-500',
    description: 'Connect X account for DMs and mentions',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'text', required: true },
      { key: 'api_secret', label: 'API Secret', type: 'password', required: true },
      { key: 'access_token', label: 'Access Token', type: 'text', required: true },
      { key: 'access_token_secret', label: 'Access Token Secret', type: 'password', required: true },
      { key: 'bearer_token', label: 'Bearer Token', type: 'password', required: true }
    ],
    docsUrl: 'https://developer.twitter.com/en/docs/twitter-api/getting-started/getting-access-to-the-twitter-api'
  },
  youtube: {
    name: 'YouTube',
    icon: Youtube,
    color: 'bg-red-600',
    description: 'Monitor YouTube comments and respond',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'text', required: true },
      { key: 'channel_id', label: 'Channel ID', type: 'text', required: false },
      { key: 'access_token', label: 'OAuth Access Token (for replying)', type: 'password', required: false }
    ],
    docsUrl: 'https://developers.google.com/youtube/v3/getting-started'
  },
  website_chat: {
    name: 'Website Live Chat',
    icon: Globe,
    color: 'bg-indigo-500',
    description: 'Embed live chat widget on your website',
    fields: [
      { key: 'widget_title', label: 'Widget Title', type: 'text', required: false, default: 'Chat with us' },
      { key: 'welcome_message', label: 'Welcome Message', type: 'text', required: false },
      { key: 'primary_color', label: 'Primary Color', type: 'color', required: false, default: '#4F46E5' }
    ],
    hasWidget: true
  },
  email_microsoft365: {
    name: 'Microsoft 365 Email',
    icon: Mail,
    color: 'bg-blue-500',
    description: 'Send and receive emails via Microsoft 365',
    isOAuth: true,
    settingsUrl: '/email-settings',
    fields: [
      { key: 'tenant_id', label: 'Tenant ID (Directory ID)', type: 'text', required: true, placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
      { key: 'client_id', label: 'Application (Client) ID', type: 'text', required: true, placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
      { key: 'client_secret', label: 'Client Secret', type: 'password', required: true },
      { key: 'shared_mailbox', label: 'Shared Mailbox Email', type: 'email', required: false, placeholder: 'crm@yourcompany.com' }
    ],
    docsUrl: 'https://docs.microsoft.com/en-us/azure/active-directory/develop/quickstart-register-app'
  }
};

const ChannelSettings = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [configValues, setConfigValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [showWidgetCode, setShowWidgetCode] = useState(false);

  useEffect(() => {
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      const response = await axios.get(`${API}/omnichannel/channels?token=${token}`);
      setChannels(response.data || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
      toast.error('Failed to load channels');
    } finally {
      setLoading(false);
    }
  };

  const openConfigDialog = (channelId) => {
    setSelectedChannel(channelId);
    setConfigValues({});
    setShowConfigDialog(true);
  };

  const handleConfigChange = (key, value) => {
    setConfigValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveConfig = async () => {
    if (!selectedChannel) return;

    const config = channelConfig[selectedChannel];
    if (!config) return;

    // Validate required fields
    for (const field of config.fields) {
      if (field.required && !configValues[field.key]) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    setSaving(true);
    try {
      let endpoint = '';
      let payload = { ...configValues };

      if (selectedChannel === 'whatsapp') {
        endpoint = `${API}/whatsapp/configure`;
      } else if (selectedChannel === 'facebook' || selectedChannel === 'instagram') {
        endpoint = `${API}/social/${selectedChannel}/configure`;
      } else if (selectedChannel === 'twitter') {
        endpoint = `${API}/social/twitter/configure`;
      } else if (selectedChannel === 'youtube') {
        endpoint = `${API}/social/youtube/configure`;
      } else if (selectedChannel === 'website_chat') {
        endpoint = `${API}/omnichannel/channels/${selectedChannel}/configure`;
        payload = { enabled: true, settings: configValues };
      }

      await axios.post(`${endpoint}?token=${token}`, payload);
      
      toast.success(`${config.name} configured successfully!`);
      setShowConfigDialog(false);
      fetchChannels();
    } catch (error) {
      console.error('Config save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async (channelId) => {
    if (!confirm(`Are you sure you want to disconnect ${channelConfig[channelId]?.name}?`)) {
      return;
    }

    try {
      let endpoint = '';
      if (channelId === 'whatsapp') {
        endpoint = `${API}/whatsapp/disconnect`;
      } else {
        endpoint = `${API}/omnichannel/channels/${channelId}/disconnect`;
      }

      await axios.post(`${endpoint}?token=${token}`);
      toast.success('Channel disconnected');
      fetchChannels();
    } catch (error) {
      toast.error('Failed to disconnect channel');
    }
  };

  const getWidgetCode = () => {
    // Generate embeddable widget code
    return `<!-- Treppan CRM Live Chat Widget -->
<script>
  (function() {
    var w = window;
    var d = document;
    var s = d.createElement('script');
    s.src = '${BACKEND_URL}/widget/chat.js';
    s.async = true;
    s.dataset.accountId = 'YOUR_ACCOUNT_ID';
    d.head.appendChild(s);
  })();
</script>`;
  };

  const ChannelCard = ({ channel }) => {
    const config = channelConfig[channel.id];
    if (!config) return null;

    const Icon = config.icon;
    const isConnected = channel.configured && channel.enabled;

    return (
      <Card className={`relative overflow-hidden ${isConnected ? 'border-green-200' : ''}`}>
        {isConnected && (
          <div className="absolute top-0 right-0 px-2 py-1 bg-green-500 text-white text-xs rounded-bl">
            Connected
          </div>
        )}
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-lg ${config.color}`}>
              <Icon size={24} className="text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">{config.name}</CardTitle>
              <CardDescription className="text-xs">{config.description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mt-2">
            {config.isTwilio ? (
              // Twilio WhatsApp - redirect to dedicated settings page
              <Button 
                className={`flex-1 ${config.color} hover:opacity-90`}
                size="sm"
                onClick={() => navigate(config.settingsUrl)}
              >
                <Settings size={14} className="mr-1" /> {isConnected ? 'Configure' : 'Setup'}
              </Button>
            ) : isConnected ? (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => openConfigDialog(channel.id)}
                >
                  <Settings size={14} className="mr-1" /> Settings
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleDisconnect(channel.id)}
                >
                  <X size={14} className="mr-1" /> Disconnect
                </Button>
              </>
            ) : (
              <Button 
                className={`flex-1 ${config.color} hover:opacity-90`}
                size="sm"
                onClick={() => openConfigDialog(channel.id)}
              >
                <Link2 size={14} className="mr-1" /> Connect
              </Button>
            )}
          </div>
          
          {channel.id === 'website_chat' && isConnected && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full mt-2 text-indigo-600"
              onClick={() => setShowWidgetCode(true)}
            >
              <FileText size={14} className="mr-1" /> Get Widget Code
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="channel-settings-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Channel Integrations</h1>
        <p className="text-slate-500 mt-1">
          Connect your social media accounts and communication channels
        </p>
      </div>

      {/* Info Alert */}
      <Alert className="mb-6 bg-blue-50 border-blue-200">
        <AlertTriangle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-700">
          API credentials are required for each platform. Click the documentation link in each configuration dialog for setup instructions.
        </AlertDescription>
      </Alert>

      {/* Channel Tabs */}
      <Tabs defaultValue="messaging" className="space-y-6">
        <TabsList>
          <TabsTrigger value="messaging">Messaging</TabsTrigger>
          <TabsTrigger value="social">Social Media</TabsTrigger>
          <TabsTrigger value="website">Website</TabsTrigger>
        </TabsList>

        <TabsContent value="messaging" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {channels.filter(c => ['whatsapp', 'email'].includes(c.id)).map(channel => (
              <ChannelCard key={channel.id} channel={channel} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="social" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {channels.filter(c => ['facebook', 'instagram', 'twitter', 'youtube'].includes(c.id)).map(channel => (
              <ChannelCard key={channel.id} channel={channel} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="website" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {channels.filter(c => ['website_chat', 'website_form'].includes(c.id)).map(channel => (
              <ChannelCard key={channel.id} channel={channel} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Configuration Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedChannel && channelConfig[selectedChannel] && (
                <>
                  {(() => {
                    const Icon = channelConfig[selectedChannel].icon;
                    return <Icon size={20} className="text-slate-600" />;
                  })()}
                  Configure {channelConfig[selectedChannel]?.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Enter your API credentials to connect this channel
            </DialogDescription>
          </DialogHeader>

          {selectedChannel && channelConfig[selectedChannel] && (
            <div className="space-y-4 py-4">
              {/* Documentation Link */}
              <a
                href={channelConfig[selectedChannel].docsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                <ExternalLink size={14} />
                View setup documentation
              </a>

              {/* Fields */}
              {channelConfig[selectedChannel].fields.map((field) => (
                <div key={field.key} className="space-y-1">
                  <Label htmlFor={field.key} className="text-sm">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  <Input
                    id={field.key}
                    type={field.type}
                    placeholder={field.default || `Enter ${field.label.toLowerCase()}`}
                    value={configValues[field.key] || ''}
                    onChange={(e) => handleConfigChange(field.key, e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveConfig} disabled={saving}>
              {saving ? (
                <>
                  <RefreshCw className="animate-spin mr-2" size={14} />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={14} className="mr-2" />
                  Save & Connect
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Widget Code Dialog */}
      <Dialog open={showWidgetCode} onOpenChange={setShowWidgetCode}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Website Chat Widget Code</DialogTitle>
            <DialogDescription>
              Copy and paste this code into your website's HTML, just before the closing &lt;/body&gt; tag
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-900 p-4 rounded-lg overflow-x-auto">
            <pre className="text-green-400 text-sm whitespace-pre-wrap">
              {getWidgetCode()}
            </pre>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                navigator.clipboard.writeText(getWidgetCode());
                toast.success('Code copied to clipboard!');
              }}
            >
              Copy Code
            </Button>
            <Button onClick={() => setShowWidgetCode(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChannelSettings;
